import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { selectedTabContext } from '../../../Context/SelectedTabContext';
import { ShopContext } from '../../../Context/ShopContext';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import AccountServices from '../../../services/AccountServices';
import { AlertMessagesContext } from 'react-alert-messages';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';
import CashInOutDialog from '../../popups/CashInOutDialog';
import DateTimeRange from '../../utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from '../../../services/DynamicDateRangeService';
import ShiftService from '../../../services/ShiftService';

function AccountLedgerPage() {
  const { t } = useTranslation('common');
  const { setSelectedTab, setSelectedSubTab, setSections } = useContext(selectedTabContext);
  const { cachedAccountHeads, cachedLedgers, setCachedLedgers, setCachedAccountHeads } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const location = useLocation();

  const getQueryParam = useCallback(
    (name) => {
      return new URLSearchParams(location.search).get(name);
    },
    [location.search]
  );

  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [accountHeads, setAccountHeads] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedAccountHead, setSelectedAccountHead] = useState(getQueryParam('head') || 'all');
  const [selectedShift, setSelectedShift] = useState('all');
  const { startDate: initialStart, endDate: initialEnd } = dynamicDateRange('ACCOUNT_LEDGER');
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [loading, setLoading] = useState(false);
  const [showCashInOutDialog, setShowCashInOutDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchAccountHeads = useCallback(
    async (force = false) => {
      if (!force && cachedAccountHeads) {
        setAccountHeads(cachedAccountHeads);
        return;
      }
      try {
        const data = await AccountServices.getAccountHeads();
        const results = data.results || data || [];
        setAccountHeads(results);
        if (setCachedAccountHeads) {
          setCachedAccountHeads(results);
        }
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
    },
    [postAlertMessage, cachedAccountHeads, setCachedAccountHeads]
  );

  const fetchShifts = useCallback(async () => {
    try {
      const params = {};
      if (startDate) params.start_date = DateTimeHelpers.getTimezoneOffsetDate(startDate);
      if (endDate) params.end_date = DateTimeHelpers.getTimezoneOffsetDate(endDate);
      const data = await ShiftService.getShifts({ ...params, limit: 1000 });
      setShifts(data.results || data || []);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  }, [startDate, endDate, postAlertMessage]);

  const fetchLedger = useCallback(
    async (force = false) => {
      const startStr = startDate ? new Date(startDate).getTime() : 'none';
      const endStr = endDate ? new Date(endDate).getTime() : 'none';
      const cacheKey = `ledger_${selectedAccountHead}_${startStr}_${endStr}_${selectedShift}`;

      if (!force && cachedLedgers && cachedLedgers[cacheKey]) {
        setLedgerEntries(cachedLedgers[cacheKey]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params = {};
        if (startDate) {
          params.start_date = DateTimeHelpers.getTimezoneOffsetDate(startDate);
        }
        if (endDate) {
          params.end_date = DateTimeHelpers.getTimezoneOffsetDate(endDate);
        }
        if (selectedAccountHead && selectedAccountHead !== 'all') {
          params.account_head = selectedAccountHead;
        }
        if (selectedShift && selectedShift !== 'all') {
          params.shift_id = selectedShift;
        }
        const data = await AccountServices.getAccountLedger({ ...params, limit: 5000 });
        const results = data.results || data || [];
        setLedgerEntries(results);
        if (setCachedLedgers) {
          setCachedLedgers((prev) => ({ ...prev, [cacheKey]: results }));
        }
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoading(false);
    },
    [startDate, endDate, selectedAccountHead, selectedShift, postAlertMessage, cachedLedgers, setCachedLedgers]
  );

  const handleSync = async () => {
    setLoading(true);
    try {
      const res = await AccountServices.syncAccountLedger();
      postAlertMessage({ text: res.message, type: 'success' });
      setCachedLedgers({});
      fetchLedger(true);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoading(false);
  };

  useEffect(() => {
    setSelectedTab('account-ledger');
    setSelectedSubTab('');
    setSections((prev) => ({ ...prev, management: true }));
    fetchAccountHeads();
  }, [setSelectedTab, setSelectedSubTab, setSections, fetchAccountHeads]);

  useEffect(() => {
    const headId = getQueryParam('head');
    if (headId) {
      setSelectedAccountHead(headId);
    }
  }, [location.search, getQueryParam]);

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate ? startDate.getTime() : null, endDate ? endDate.getTime() : null, selectedAccountHead, selectedShift]);

  useEffect(() => {
    fetchShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate ? startDate.getTime() : null, endDate ? endDate.getTime() : null]);


  const getTransactionCategory = useCallback(
    (entry) => {
      if (!entry) return null;
      const reference = (entry.reference || '').toLowerCase().trim();
      const description = (entry.description || '').toLowerCase().trim();
      const accountName = (entry.account_head_name || '').toLowerCase().trim();
      const entryHeadId = entry.account_head;

      // 1. Check associated account head type first for strongest match
      if (entryHeadId && Array.isArray(accountHeads)) {
        const matchedHead = accountHeads.find((h) => h.id === entryHeadId);
        if (matchedHead) {
          if (matchedHead.type === 'SUPPLIER') {
            return { label: t('Vendors'), color: '#D97706' };
          }
          if (matchedHead.type === 'CUSTOMER') {
            return { label: t('Customers'), color: '#0891B2' };
          }
          if (matchedHead.type === 'EXPENSE') {
            return { label: t('Expenses'), color: '#F59E0B' };
          }
        }
      }

      // 2. Specific multi-word keywords
      if (
        reference.includes('purchase return') ||
        description.includes('purchase return') ||
        accountName.includes('purchase return') ||
        reference.includes('purchase_return') ||
        description.includes('purchase_return')
      ) {
        return { label: t('Purchase Return'), color: '#DC2626' };
      }

      const combinedStr = `${reference} ${description} ${accountName}`;
      if (
        combinedStr.includes('void') ||
        combinedStr.includes('sale return') ||
        combinedStr.includes('sale_return') ||
        combinedStr.includes('refund') ||
        combinedStr.includes('cancel')
      ) {
        return { label: t('Void / Sale Return'), color: '#EF4444' };
      }

      if (
        reference.includes('balance transfer') ||
        description.includes('balance transfer') ||
        reference.includes('balancetransfer') ||
        description.includes('balancetransfer')
      ) {
        return { label: t('Balance Transfer'), color: '#7C3AED' };
      }

      if (reference.includes('expense') || description.includes('expense') || accountName.includes('expense')) {
        return { label: t('Expenses'), color: '#F59E0B' };
      }

      if (
        entry.type === 'in' ||
        entry.type === 'out' ||
        reference.includes('cash in') ||
        description.includes('cash in') ||
        reference.includes('cash_in') ||
        description.includes('cash_in') ||
        reference.includes('cash out') ||
        description.includes('cash out') ||
        reference.includes('cash_out') ||
        description.includes('cash_out') ||
        description.includes('owner investment') ||
        description.includes('drawings by owner') ||
        description.includes('cash deposit') ||
        description.includes('petty cash') ||
        description.includes('pettycash') ||
        description.includes('withdrawal') ||
        description.includes('deposit') ||
        reference.includes('deposit') ||
        reference.includes('withdrawal')
      ) {
        return { label: t('Deposit / Withdrawal'), color: '#475569' };
      }

      // 3. Single-word keywords
      if (reference.includes('purchase') || description.includes('purchase') || accountName.includes('purchase')) {
        return { label: t('Purchase'), color: '#2563EB' };
      }

      if (reference.includes('sale') || description.includes('sale') || accountName.includes('sale')) {
        return { label: t('Sales'), color: '#22C55E' };
      }

      if (reference.includes('transfer') || description.includes('transfer') || accountName.includes('transfer')) {
        return { label: t('Transfer'), color: '#7C3AED' };
      }

      if (
        reference.includes('vendor') ||
        description.includes('vendor') ||
        accountName.includes('vendor') ||
        reference.includes('supplier') ||
        description.includes('supplier') ||
        accountName.includes('supplier')
      ) {
        return { label: t('Vendors'), color: '#D97706' };
      }

      if (
        reference.includes('customer') ||
        description.includes('customer') ||
        accountName.includes('customer') ||
        reference.includes('client') ||
        description.includes('client') ||
        accountName.includes('client')
      ) {
        return { label: t('Customers'), color: '#0891B2' };
      }

      return null;
    },
    [accountHeads, t]
  );

  const displayEntries = React.useMemo(() => {
    let filteredLedgerEntries = ledgerEntries;
    if (selectedShift && selectedShift !== 'all') {
      filteredLedgerEntries = ledgerEntries.filter((entry) => String(entry.shift_id) === String(selectedShift));
    }
    if (selectedAccountHead !== 'all') {
      filteredLedgerEntries = filteredLedgerEntries.filter(
        (entry) => String(entry.account_head) === String(selectedAccountHead)
      );
    }

    let baseEntries = [...filteredLedgerEntries];

    // Sort chronologically (oldest first)
    baseEntries.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      if (dateA - dateB !== 0) return dateA - dateB;
      return a.id - b.id;
    });

    if (selectedCategory !== 'all') {
      baseEntries = baseEntries.filter((entry) => {
        const category = getTransactionCategory(entry);
        return category && category.label === selectedCategory;
      });
    }

    return baseEntries;
  }, [ledgerEntries, selectedAccountHead, selectedShift, selectedCategory, getTransactionCategory]);

  const periodOpeningBalance =
    displayEntries.length > 0 && selectedAccountHead !== 'all'
      ? displayEntries[0].balance - (Number(displayEntries[0].debit || 0) - Number(displayEntries[0].credit || 0))
      : 0;

  const periodClosingBalance =
    displayEntries.length > 0 && selectedAccountHead !== 'all'
      ? displayEntries[displayEntries.length - 1].balance
      : 0;

  const totalDebit = displayEntries.reduce((sum, entry) => sum + Number(entry.debit || 0), 0);
  const totalCredit = displayEntries.reduce((sum, entry) => sum + Number(entry.credit || 0), 0);

  return (
    <div style={{ padding: '20px' }}>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="h5" component="h1">
            {t('Account Ledger')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
          style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '10px' }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowCashInOutDialog(true)}
            style={{ backgroundColor: '#00a65a', color: '#fff' }}
          >
            {t('New Transactions')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SyncIcon />}
            onClick={handleSync}
            disabled={loading}
          >
            {t('Reload')}
          </Button>
        </Grid>
      </Grid>

      <Paper style={{ padding: '15px', marginBottom: '20px' }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <div style={{ paddingBottom: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>{t('Account Head')}</label>
            </div>
            <TextField
              select
              variant="outlined"
              size="small"
              fullWidth
              value={selectedAccountHead}
              onChange={(e) => setSelectedAccountHead(e.target.value)}
              SelectProps={{
                MenuProps: {
                  MenuListProps: {
                    sx: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      width: '100%',
                    },
                  },
                },
              }}
            >
              <MenuItem
                value="all"
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start !important',
                  padding: '10px 14px 4px !important',
                }}
              >
                {t('All Accounts')}
              </MenuItem>
              {(Array.isArray(accountHeads) ? accountHeads : []).map((head) => (
                <MenuItem
                  key={head.id}
                  value={head.id}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start !important',
                    padding: '10px 14px 4px !important',
                  }}
                >
                  {head.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <DateTimeRange setFilterFromDate={setStartDate} setFilterToDate={setEndDate} storageKey="ACCOUNT_LEDGER" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <div style={{ paddingBottom: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>{t('Shift')}</label>
            </div>
            <TextField
              select
              variant="outlined"
              size="small"
              fullWidth
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
              SelectProps={{
                MenuProps: {
                  MenuListProps: {
                    sx: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      width: '100%',
                    },
                  },
                },
              }}
            >
              <MenuItem
                value="all"
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start !important',
                  padding: '10px 14px 4px !important',
                }}
              >
                {t('All Shifts')}
              </MenuItem>
              {shifts.map((shift) => (
                <MenuItem
                  key={shift.id}
                  value={String(shift.id)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start !important',
                    padding: '10px 14px 4px !important',
                  }}
                >
                  {shift.name ? `${shift.name} (${shift.id})` : `Shift ${shift.id}`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Transaction Type Legend */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          padding: '10px 15px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          marginBottom: '15px',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>{t('Legend:')}</span>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            opacity: selectedCategory === 'all' ? 1 : 0.4,
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: selectedCategory === 'all' ? '#e2e8f0' : 'transparent',
            transition: 'all 0.2s ease',
          }}
          onClick={() => setSelectedCategory('all')}
          title={t('Show All Transactions')}
        >
          <span style={{ fontSize: '12px', fontWeight: selectedCategory === 'all' ? 600 : 500, color: '#333' }}>
            {t('All')}
          </span>
        </div>

        {[
          { label: t('Sales'), color: '#22C55E' },
          { label: t('Void / Sale Return'), color: '#EF4444' },
          { label: t('Purchase'), color: '#2563EB' },
          { label: t('Purchase Return'), color: '#DC2626' },
          { label: t('Balance Transfer'), color: '#7C3AED' },
          { label: t('Vendors'), color: '#D97706' },
          { label: t('Customers'), color: '#0891B2' },
          { label: t('Deposit / Withdrawal'), color: '#475569' },
          { label: t('Expenses'), color: '#F59E0B' },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              opacity: selectedCategory === 'all' || selectedCategory === item.label ? 1 : 0.4,
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: selectedCategory === item.label ? `${item.color}15` : 'transparent',
              transition: 'all 0.2s ease',
            }}
            onClick={() => setSelectedCategory((prev) => (prev === item.label ? 'all' : item.label))}
            title={`Filter by ${item.label}`}
          >
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: item.color,
                display: 'inline-block',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: selectedCategory === item.label ? 600 : 500, color: '#333' }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>{t('Date & Time')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('Account')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>{t('Description')}</TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                {t('Debit')}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                {t('Credit')}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                {t('Balance')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('Loading...')}
                </TableCell>
              </TableRow>
            ) : displayEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('No Ledger Entries Found')}
                </TableCell>
              </TableRow>
            ) : (
              <>
                <TableRow style={{ backgroundColor: '#eeeeee', fontWeight: 'bold' }}>
                  <TableCell>{DateTimeHelpers.formatDateTime(startDate)}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>{t('Opening Balance')}</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right" style={{ fontWeight: 'bold' }}>
                    {selectedAccountHead !== 'all' ? periodOpeningBalance.toFixed(2) : '-'}
                  </TableCell>
                </TableRow>
                {displayEntries.map((entry) => {
                  const category = getTransactionCategory(entry);
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>{`${DateTimeHelpers.convertDateToIsoDMY(entry.date)} ${DateTimeHelpers.getTime(entry.time)}`}</TableCell>
                      <TableCell>{entry.account_head_name}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          {category && (
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: category.color,
                                display: 'inline-block',
                                flexShrink: 0,
                              }}
                              title={category.label}
                            />
                          )}
                          <span>{entry.description || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell align="right">{Number(entry.debit).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(entry.credit).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        {selectedCategory === 'all' && entry.balance !== null ? Number(entry.balance).toFixed(2) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow style={{ backgroundColor: '#eeeeee', fontWeight: 'bold' }}>
                  <TableCell>{DateTimeHelpers.formatDateTime(endDate)}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>{t('Closing Balance')}</TableCell>
                  <TableCell align="right">{selectedCategory === 'all' && selectedAccountHead !== 'all' ? '-' : totalDebit.toFixed(2)}</TableCell>
                  <TableCell align="right">{selectedCategory === 'all' && selectedAccountHead !== 'all' ? '-' : totalCredit.toFixed(2)}</TableCell>
                  <TableCell align="right" style={{ fontWeight: 'bold' }}>
                    {selectedAccountHead !== 'all'
                      ? selectedCategory === 'all'
                        ? periodClosingBalance.toFixed(2)
                        : (totalDebit - totalCredit).toFixed(2)
                      : (totalDebit - totalCredit).toFixed(2)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showCashInOutDialog && (
        <CashInOutDialog
          defaultAccountHeadId={selectedAccountHead !== 'all' ? selectedAccountHead : ''}
          onClose={() => setShowCashInOutDialog(false)}
          onSuccess={() => {
            setShowCashInOutDialog(false);
            setCachedLedgers({});
            fetchLedger(true);
            fetchAccountHeads(true);
          }}
        />
      )}
    </div>
  );
}

export default withConsoleBase(AccountLedgerPage);
